import React, { useState, useEffect, useRef } from 'react';
import { Terminal, X, Minimize2, Maximize2, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Singleton pour gérer les logs globaux
class GlobalConsoleManager {
  constructor() {
    this.listeners = [];
    this.logs = [];
    this.maxLogs = 100;
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  addLog(log) {
    const timestamp = new Date().toLocaleTimeString('fr-FR');
    const newLog = {
      id: Date.now(),
      timestamp,
      ...log
    };
    this.logs = [...this.logs.slice(-this.maxLogs + 1), newLog];
    this.listeners.forEach(callback => callback(this.logs));
  }

  clear() {
    this.logs = [];
    this.listeners.forEach(callback => callback(this.logs));
  }

  getLogs() {
    return this.logs;
  }
}

// Instance singleton exportée
export const consoleManager = new GlobalConsoleManager();

// Fonction utilitaire pour ajouter des logs facilement
export const addGlobalLog = (type, module, message, details = null) => {
  consoleManager.addLog({ type, module, message, details });
};

// Types de logs avec couleurs
const LOG_TYPES = {
  info: { color: 'text-marine-cyan', bg: 'bg-marine-cyan/10', icon: 'INFO' },
  success: { color: 'text-marine-green', bg: 'bg-marine-green/10', icon: 'OK' },
  warning: { color: 'text-marine-amber', bg: 'bg-marine-amber/10', icon: 'WARN' },
  error: { color: 'text-red-400', bg: 'bg-red-500/10', icon: 'ERR' },
  progress: { color: 'text-purple-400', bg: 'bg-purple-500/10', icon: '...' },
  api: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: 'API' },
  data: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: 'DATA' },
};

const GlobalConsole = () => {
  const [logs, setLogs] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    // S'abonner aux logs
    const unsubscribe = consoleManager.subscribe((newLogs) => {
      setLogs(newLogs);
      if (isMinimized || !isExpanded) {
        setUnreadCount(prev => prev + 1);
      }
    });

    // Charger les logs existants
    setLogs(consoleManager.getLogs());

    return unsubscribe;
  }, [isMinimized, isExpanded]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current && isExpanded) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, isExpanded]);

  // Reset unread count when expanded
  useEffect(() => {
    if (isExpanded && !isMinimized) {
      setUnreadCount(0);
    }
  }, [isExpanded, isMinimized]);

  const handleToggle = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
    }
    setUnreadCount(0);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(true);
    setIsExpanded(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsExpanded(false);
    setIsMinimized(true);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    consoleManager.clear();
  };

  return (
    <div className="fixed bottom-2 right-2 md:bottom-4 md:right-4 z-50 font-mono hidden md:block" data-testid="global-console">
      <AnimatePresence>
        {/* Expanded Console */}
        {isExpanded && !isMinimized && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="w-[90vw] md:w-[500px] max-w-[500px] bg-black/95 border-2 border-marine-cyan/50 rounded-lg shadow-2xl shadow-marine-cyan/20 overflow-hidden"
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-3 py-2 bg-marine-navy/80 border-b border-marine-cyan/30 cursor-pointer"
              onClick={handleToggle}
            >
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-marine-cyan" />
                <span className="text-marine-cyan text-sm font-bold tracking-wider">CONSOLE SYSTÈME</span>
                <span className="text-white/80 text-xs">({logs.length} entrées)</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleClear}
                  className="p-1 text-white/80 hover:text-marine-amber hover:bg-marine-amber/20 rounded text-xs"
                  title="Effacer"
                >
                  CLR
                </button>
                <button
                  onClick={handleMinimize}
                  className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded"
                  title="Minimiser"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleClose}
                  className="p-1 text-white/80 hover:text-red-400 hover:bg-red-500/20 rounded"
                  title="Fermer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Logs */}
            <div 
              ref={scrollRef}
              className="h-[250px] overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-marine-cyan/30"
            >
              {logs.length === 0 ? (
                <div className="text-white/30 text-xs text-center py-8">
                  En attente d'activité...
                </div>
              ) : (
                logs.map((log) => {
                  const logStyle = LOG_TYPES[log.type] || LOG_TYPES.info;
                  return (
                    <div
                      key={log.id}
                      className={`flex items-start gap-2 text-xs p-1.5 rounded ${logStyle.bg}`}
                    >
                      <span className="text-white/40 flex-shrink-0">[{log.timestamp}]</span>
                      <span className={`flex-shrink-0 font-bold ${logStyle.color}`}>
                        [{log.module?.substring(0, 8).padEnd(8) || 'SYSTEM  '}]
                      </span>
                      <span className={logStyle.color}>{log.message}</span>
                      {log.details && (
                        <span className="text-white/40 ml-auto flex-shrink-0">
                          {typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed Button */}
      {(!isExpanded || isMinimized) && (
        <motion.button
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          onClick={handleToggle}
          className={`
            flex items-center gap-2 px-4 py-2 
            bg-black/90 border-2 rounded-lg
            transition-all duration-200
            ${unreadCount > 0 
              ? 'border-marine-amber animate-pulse shadow-lg shadow-marine-amber/30' 
              : 'border-marine-cyan/50 hover:border-marine-cyan'
            }
          `}
        >
          <Terminal className={`w-5 h-5 ${unreadCount > 0 ? 'text-marine-amber' : 'text-marine-cyan'}`} />
          <span className="text-white/90 text-sm font-bold">CONSOLE</span>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-marine-amber text-black text-xs font-bold rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <ChevronUp className="w-4 h-4 text-white/80" />
        </motion.button>
      )}
    </div>
  );
};

export default GlobalConsole;
